// PRC-049 (boceto S4, diseño 33 apartado 4): «Actualizar» un seguimiento.
// Opciones: Compra recuperada — cerrar · Sin cambios — reprogramar revisión ·
// Causa identificada — archivar · Añadir nota. Todo queda en la cronología.
// El diálogo no escribe: devuelve la decisión a quien lo abre.
// Cambio PRC-049: «Causa identificada — archivar» pide lo mismo que el panel
// «Gestión del aviso» (6.1): motivo, referencia sustitutiva con sugerencias y
// conclusión generada y editable.
import React, { useEffect, useMemo, useState } from 'react';
import { Box, ButtonBase, Dialog, IconButton, InputBase } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { useQuery } from '@tanstack/react-query';
import { getMarginCenterFollowUp, getMarginCenterSubstituteProducts } from '../../services/marginCenterService';
import type { SubstituteProductItem } from '../../types/cadenceAttention';
import ProaDatePicker from '../../../../components/Common/ProaDatePicker';
import { J } from '../../../pricingJourney/journeyTokens';
import { formatEuro, formatInteger, formatPercent } from '../../../pricingJourney/formatNumber';
import type { MarginCenterFollowUpInventoryV2Item } from '../../types/followUpInventory';
import {
  CAUSE_REASONS,
  REVIEW_SHORTCUTS,
  SUBSTITUTION_REASON,
  buildCauseConclusion,
  formatDayDate,
  reviewDateFor,
  reviewDateToIso,
  toDateOnly,
  type ReviewShortcut,
} from './cadenceManagementTexts';

export type FollowUpUpdateChoice = 'recovered' | 'reschedule' | 'cause' | 'note';

export type FollowUpUpdateDecision =
  | { kind: 'recovered'; conclusion: string }
  | { kind: 'reschedule'; nextReviewIso: string }
  | { kind: 'cause'; reason: string; substituteProductId: string | null; conclusion: string }
  | { kind: 'note'; content: string };

export const UPDATE_OPTIONS: Array<{ id: FollowUpUpdateChoice; label: string }> = [
  { id: 'recovered', label: 'Compra recuperada — cerrar' },
  { id: 'reschedule', label: 'Sin cambios — reprogramar revisión' },
  { id: 'cause', label: 'Causa identificada — archivar' },
  { id: 'note', label: 'Añadir nota' },
];

/** Conclusión al cerrar por compra recuperada. */
export const recoveredConclusion = (item: Pick<MarginCenterFollowUpInventoryV2Item, 'recovered_on' | 'recovered_units'>): string =>
  item.recovered_on
    ? `Compra recuperada el ${formatDayDate(item.recovered_on)}${item.recovered_units != null ? ` · ${formatInteger(item.recovered_units)} uds.` : ''}.`
    : 'Compra recuperada.';

const suggestionNote = (item: SubstituteProductItem): string => {
  if (item.kind === 'new') {
    const days = item.purchase_days_after ?? 0;
    return `nueva desde ${formatDayDate(item.first_purchase_after ?? null)} · ${formatInteger(days)} ${days === 1 ? 'día' : 'días'} con compra · ${formatEuro(item.amount_after ?? null, 0)}`;
  }
  if (item.kind === 'increased') {
    return item.units_change_pct != null ? `${formatPercent(item.units_change_pct, 0, { signed: true })} de unidades` : 'más unidades';
  }
  return '';
};

const useDebounced = (value: string, ms = 250) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return debounced;
};

const lblSx = { fontSize: 12, fontWeight: 700, color: J.body, m: '14px 0 6px', fontFamily: J.font } as const;
const inputSx = {
  width: '100%', border: '1px solid #334155', borderRadius: '10px', px: '12px', py: '7px',
  fontSize: 13.5, color: J.text, bgcolor: 'rgba(2,6,23,.5)', fontFamily: J.font,
} as const;

const Chip: React.FC<{ on: boolean; onClick: () => void; children: React.ReactNode }> = ({ on, onClick, children }) => (
  <ButtonBase
    onClick={onClick}
    aria-pressed={on}
    sx={{
      border: '1px solid', borderColor: on ? J.sky : '#334155', borderRadius: 999, px: '12px', py: '5px',
      fontSize: 12.5, fontWeight: 600, fontFamily: J.font, color: on ? J.skyText : J.body,
      bgcolor: on ? 'rgba(56,189,248,.14)' : 'transparent',
    }}
  >
    {children}
  </ButtonBase>
);

interface Props {
  item: MarginCenterFollowUpInventoryV2Item | null;
  initialChoice?: FollowUpUpdateChoice;
  onClose: () => void;
  onConfirm: (item: MarginCenterFollowUpInventoryV2Item, decision: FollowUpUpdateDecision) => void;
}

const FollowUpUpdateDialog: React.FC<Props> = ({ item, initialChoice = 'reschedule', onClose, onConfirm }) => {
  const [choice, setChoice] = useState<FollowUpUpdateChoice>(initialChoice);
  const [review, setReview] = useState<ReviewShortcut | null>(null);
  const [otherDate, setOtherDate] = useState('');
  const [reason, setReason] = useState<string | null>(null);
  const [conclusion, setConclusion] = useState('');
  const [edited, setEdited] = useState(false);
  const [note, setNote] = useState('');
  const [attempted, setAttempted] = useState(false);
  const [substitute, setSubstitute] = useState<SubstituteProductItem | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!item) return;
    setChoice(initialChoice); setReview(null); setOtherDate(''); setReason(null);
    setConclusion(''); setEdited(false); setNote(''); setAttempted(false);
    setSubstitute(null); setSearch('');
  }, [item, initialChoice]);

  // Sugerencias desde la última compra del aviso vinculado (si lo hay) o desde la apertura.
  const relationship = Boolean(item && item.scope === 'customer_product' && item.customer_id && item.product_id);
  const wantsSubstitute = relationship && choice === 'cause' && reason === SUBSTITUTION_REASON;
  const detail = useQuery({
    queryKey: ['margin-center', 'follow-ups', 'detail', item?.id],
    queryFn: () => getMarginCenterFollowUp(item!.id),
    enabled: wantsSubstitute,
    staleTime: 60 * 1000,
  });
  // Aviso de origen primero; si no, el más reciente.
  const linkedLastPurchase = [...(detail.data?.signal_links ?? [])]
    .sort((a, b) => Number(b.link_type === 'origin') - Number(a.link_type === 'origin')
      || String(b.linked_at).localeCompare(String(a.linked_at)))
    .map((l) => l.signal_snapshot?.last_purchase_date)
    .find((v): v is string => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)) ?? null;
  const since = linkedLastPurchase ?? (item ? toDateOnly(new Date(item.created_at)) : null);
  const debouncedSearch = useDebounced(search.trim());
  const searching = debouncedSearch.length >= 2;
  const substitutes = useQuery({
    queryKey: ['margin-center', 'cadence-management', 'substitutes', item?.customer_id, item?.product_id, since, debouncedSearch],
    queryFn: () => getMarginCenterSubstituteProducts(item!.customer_id!, {
      since, excludeProductId: item!.product_id, q: searching ? debouncedSearch : null, limit: searching ? 8 : 3,
    }),
    enabled: wantsSubstitute && !detail.isPending && (searching || Boolean(since)),
    staleTime: 60 * 1000,
  });

  useEffect(() => {
    if (!item || edited) return;
    if (choice === 'recovered') setConclusion(recoveredConclusion(item));
    else if (choice === 'cause' && reason) {
      setConclusion(buildCauseConclusion(reason, item.product_id ?? '', reason === SUBSTITUTION_REASON && substitute
        ? { product_id: substitute.product_id, product_label: substitute.product_label, since: substitute.first_purchase_after ?? null }
        : null));
    }
  }, [choice, reason, substitute, item, edited]);

  const reviewDate = review === 'other' ? otherDate : review ? reviewDateFor(review) : null;
  const error = useMemo(() => {
    if (choice === 'reschedule' && !reviewDate) return 'Elige la nueva fecha de revisión.';
    if (choice === 'cause' && !reason) return 'Elige un motivo.';
    if ((choice === 'cause' || choice === 'recovered') && !conclusion.trim()) return 'Escribe la conclusión.';
    if (choice === 'note' && !note.trim()) return 'Escribe la nota.';
    return null;
  }, [choice, reviewDate, reason, conclusion, note]);

  if (!item) return null;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setAttempted(true);
    if (error) return;
    if (choice === 'recovered') onConfirm(item, { kind: 'recovered', conclusion: conclusion.trim() });
    else if (choice === 'reschedule') onConfirm(item, { kind: 'reschedule', nextReviewIso: reviewDateToIso(reviewDate!) });
    else if (choice === 'cause') {
      onConfirm(item, {
        kind: 'cause', reason: reason!, conclusion: conclusion.trim(),
        substituteProductId: reason === SUBSTITUTION_REASON ? substitute?.product_id ?? null : null,
      });
    }
    else onConfirm(item, { kind: 'note', content: note.trim() });
  };

  const primary = choice === 'recovered' ? 'Cerrar seguimiento' : choice === 'reschedule' ? 'Reprogramar' : choice === 'cause' ? 'Archivar' : 'Añadir nota';
  const who = `${item.customer_label || item.customer_id || ''}${item.product_id ? ` · ${item.product_id}` : ''}`;

  return (
    <Dialog open={Boolean(item)} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { bgcolor: J.panel, backgroundImage: 'none', border: '1px solid rgba(148,163,184,.16)', borderRadius: '14px' } }}>
      <Box component="form" onSubmit={submit} sx={{ p: '20px 22px', fontFamily: J.font, color: J.text }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ fontSize: 11.5, fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase', color: J.skyText }}>Actualizar seguimiento</Box>
            <Box component="h2" sx={{ fontSize: 17, fontWeight: 800, m: '4px 0 0' }}>{who}</Box>
            {item.next_action && <Box sx={{ fontSize: 12, color: J.muted, mt: '2px' }}>Próxima acción: {item.next_action}{item.next_review_at ? ` · revisión ${formatDayDate(item.next_review_at)}` : ''}</Box>}
          </Box>
          <IconButton aria-label="Cerrar" onClick={onClose} sx={{ width: 32, height: 32, border: '1px solid #334155', borderRadius: '10px', color: J.skyText }}>
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', mt: '14px' }}>
          {UPDATE_OPTIONS.map((o) => (
            <Chip key={o.id} on={choice === o.id} onClick={() => { setChoice(o.id); setAttempted(false); setEdited(false); }}>{o.label}</Chip>
          ))}
        </Box>

        {choice === 'reschedule' && (
          <>
            <Box sx={lblSx}>Nueva fecha de revisión</Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {REVIEW_SHORTCUTS.map((s) => {
                const on = review === s.id;
                const date = s.id !== 'other' ? reviewDateFor(s.id) : null;
                return <Chip key={s.id} on={on} onClick={() => setReview(s.id)}>{on && date ? `${s.label} · ${formatDayDate(date)}` : s.label}</Chip>;
              })}
            </Box>
            {review === 'other' && (
              <Box sx={{ mt: '10px', maxWidth: 260 }}>
                <ProaDatePicker label="Fecha de revisión" value={otherDate} onChange={setOtherDate} size="small" minDate={toDateOnly(new Date())} />
              </Box>
            )}
          </>
        )}

        {choice === 'cause' && (
          <>
            <Box sx={lblSx}>Motivo</Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {CAUSE_REASONS.map((r) => <Chip key={r} on={reason === r} onClick={() => setReason(r)}>{r}</Chip>)}
            </Box>
            {wantsSubstitute && (
              <>
                <Box sx={lblSx}>Referencia sustitutiva <Box component="em" sx={{ fontStyle: 'normal', fontWeight: 500, color: '#64748b' }}>(opcional)</Box></Box>
                <InputBase
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar por código o descripción"
                  startAdornment={<SearchRoundedIcon sx={{ fontSize: 18, color: '#64748b', mr: 1 }} />}
                  inputProps={{ 'aria-label': 'Buscar referencia sustitutiva' }}
                  sx={inputSx}
                />
                {!searching && since && (
                  <Box sx={{ fontSize: 12, color: J.muted, m: '8px 0 6px' }}>
                    Sugeridas: referencias que este cliente empezó a comprar o incrementó desde el {formatDayDate(since)}
                  </Box>
                )}
                <Box sx={{ mt: searching ? '8px' : 0 }}>
                  {(substitutes.data?.items ?? []).map((s) => {
                    const on = substitute?.product_id === s.product_id;
                    return (
                      <ButtonBase key={s.product_id} onClick={() => setSubstitute(on ? null : s)} aria-pressed={on} sx={{
                        width: '100%', border: '1px solid', borderColor: on ? J.sky : '#233149', borderRadius: '10px',
                        p: '9px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px',
                        fontSize: 13, fontFamily: J.font, color: J.text, mb: '6px', textAlign: 'left',
                      }}>
                        <span>{on ? '● ' : ''}{s.product_id}{s.product_label && s.product_label !== s.product_id ? ` · ${s.product_label}` : ''}</span>
                        <Box component="span" sx={{ fontSize: 12, color: J.muted }}>{suggestionNote(s)}</Box>
                      </ButtonBase>
                    );
                  })}
                  {substitutes.isFetched && !(substitutes.data?.items ?? []).length && (
                    <Box sx={{ fontSize: 12, color: J.muted }}>{searching ? 'No hay referencias con ese código o descripción.' : 'No hay referencias sugeridas para este cliente.'}</Box>
                  )}
                </Box>
              </>
            )}
          </>
        )}

        {(choice === 'cause' || choice === 'recovered') && (
          <>
            <Box sx={lblSx}>Conclusión <Box component="em" sx={{ fontStyle: 'normal', fontWeight: 500, color: '#64748b' }}>(generada; editable)</Box></Box>
            <InputBase multiline minRows={2} value={conclusion} onChange={(e) => { setConclusion(e.target.value); setEdited(true); }} inputProps={{ 'aria-label': 'Conclusión', maxLength: 10000 }} sx={inputSx} />
          </>
        )}

        {choice === 'note' && (
          <>
            <Box sx={lblSx}>Nota</Box>
            <InputBase autoFocus multiline minRows={3} value={note} onChange={(e) => setNote(e.target.value)} inputProps={{ 'aria-label': 'Nota', maxLength: 4000 }} sx={inputSx} />
          </>
        )}

        {attempted && error && <Box sx={{ fontSize: 12, color: J.dangerText, mt: '8px' }}>{error}</Box>}

        <Box sx={{ pt: '18px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <ButtonBase onClick={onClose} sx={{ height: 34, px: '14px', borderRadius: '10px', fontSize: 13, fontWeight: 700, border: '1px solid #334155', color: J.text, fontFamily: J.font }}>Cancelar</ButtonBase>
          <ButtonBase type="submit" sx={{ height: 34, px: '14px', borderRadius: '10px', fontSize: 13, fontWeight: 700, bgcolor: J.sky, color: J.onSky, fontFamily: J.font }}>{primary}</ButtonBase>
        </Box>
      </Box>
    </Dialog>
  );
};

export default FollowUpUpdateDialog;
